import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, inject, input, output, signal } from '@angular/core';

import { AuthStore } from '../../core/auth';
import { BusinessSettingsStore } from '../../core/settings';
import { FloatingTableMenuController } from '../../shared/table';
import { MoneyPipe } from '../../shared/money';

import type { InventoryUnitStatus, StockTreeNode, StockTreeUnitNode, StockTreeVariantNode } from '../inventory/models/inventory.model';
import { formatInventoryDate, formatInventoryUnitStatusLabel } from '../inventory/inventory-display-presenters';
import { resolveAllowedStatusTransitions, resolveOperationalActionVisibility } from '../inventory/inventory-ui-helpers';
import { REQUIRED_COLUMN_COUNT } from './stock-columns';
import type { StockOptInColumnId } from './stock-columns';

const EM_DASH = '—';

/**
 * Issue #43 — a variant that still owns inventory units cannot be deleted.
 * Mirrors the API 409 CONFLICT message.
 */
const BLOCKED_DELETE_MESSAGE =
  'No se puede eliminar una variante que todavía tiene unidades de inventario.';

type StockRowAction = 'edit' | 'delete';

export interface StockTreeRowActionEvent {
  action: StockRowAction;
  level: 'product' | 'variant' | 'unit' | 'variant-cta' | 'unit-cta';
  productId?: string;
  variantId?: string;
  unitId?: string;
  unitLabel?: string;
}

interface RowActionItem {
  action: StockRowAction;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface ProductRow {
  key: string;
  level: 'product';
  productId: string;
  brand: string;
  model: string;
  stock: number;
  modelLabel: string;
  status: string;
  expanded: boolean;
  isEmpty: boolean;
  actions: RowActionItem[];
}

interface VariantRow {
  key: string;
  level: 'variant';
  productId: string;
  variantId: string;
  stock: number;
  modelLabel: string;
  status: string;
  capacity: string;
  price: string;
  discount: string;
  color: string;
  supplier: string;
  expanded: boolean;
  isEmpty: boolean;
  actions: RowActionItem[];
  parentExpanded: boolean;
}

interface UnitRow {
  key: string;
  level: 'unit';
  productId: string;
  variantId: string;
  unitId: string;
  stock: string;
  modelLabel: string;
  local: string;
  capacity: string;
  battery: string;
  price: string;
  discount: string;
  color: string;
  box: string;
  imei: string;
  serial: string;
  receivedAt: string;
  supplier: string;
  status: string;
  rawStatus: InventoryUnitStatus;
  isRefurbished: boolean;
  observation: string | null;
  costPrice: string;
  actions: RowActionItem[];
  parentExpanded: boolean;
}

interface VariantCtaRow {
  key: string;
  level: 'variant-cta';
  productId: string;
  brand: string;
  model: string;
}

interface UnitCtaRow {
  key: string;
  level: 'unit-cta';
  productId: string;
  variantId: string;
  brand: string;
  model: string;
  variantLabel: string;
}

type ActionableStockRow = ProductRow | VariantRow | UnitRow;

type StockRow = ActionableStockRow | VariantCtaRow | UnitCtaRow;

@Component({
  selector: 'app-stock-tree-table',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="stock-tree-table app-table-shell">
      <div class="stock-tree-table__scroller app-table-scroller" (scroll)="closeMenu(); closeObservationTooltip()">
        <table class="stock-tree-table__table app-table">
          <thead>
            <tr>
              <th class="stock-tree-table__toggle-column" aria-hidden="true"></th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Estado</th>
              <th>Stock</th>
              @if (visibleColumns().has('local')) { <th>Local</th> }
              @if (visibleColumns().has('capacidad')) { <th>Capacidad</th> }
              @if (visibleColumns().has('bateria')) { <th>Batería</th> }
              @if (visibleColumns().has('precio')) { <th>Precio</th> }
              @if (visibleColumns().has('descuento')) { <th>Descuento</th> }
              @if (canViewCostPrice() && visibleColumns().has('costo')) { <th>Costo</th> }
              @if (visibleColumns().has('color')) { <th>Color</th> }
              @if (visibleColumns().has('caja')) { <th>Caja</th> }
              @if (visibleColumns().has('imei')) { <th>IMEI</th> }
              @if (visibleColumns().has('serie')) { <th>Serie</th> }
              @if (visibleColumns().has('ingreso')) { <th>Ingreso</th> }
              @if (visibleColumns().has('proveedor')) { <th>Proveedor</th> }
              @if (visibleColumns().has('reacondicionado')) { <th>Reacondicionado</th> }
              @if (visibleColumns().has('observacion')) { <th>Observación</th> }
              <th class="stock-tree-table__actions-column app-table__actions-column">Acciones</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.key) {
              @if (row.level === 'variant-cta') {
                <tr class="stock-tree-table__cta-row">
                  <td [attr.colspan]="columnCount()">
                    <button type="button" class="stock-tree-table__cta-btn" (click)="onCreateFirstVariant(row.productId)">
                      Crea la primera variante
                    </button>
                  </td>
                </tr>
              } @else if (row.level === 'unit-cta') {
                <tr class="stock-tree-table__cta-row">
                  <td [attr.colspan]="columnCount()">
                <button type="button" class="stock-tree-table__cta-btn" (click)="onCreateFirstUnit(row.productId, row.variantId)">
                  Crea la primera unidad
                </button>
                  </td>
                </tr>
              } @else {
              <tr [class.stock-tree-table__row--product]="row.level === 'product'" [class.stock-tree-table__row--variant]="row.level === 'variant'" [class.stock-tree-table__row--sold]="row.level === 'unit' && row.rawStatus === 'SOLD'" [class.stock-tree-table__row--child-of-product]="row.level === 'variant' && row.parentExpanded" [class.stock-tree-table__row--child-of-variant]="row.level === 'unit' && row.parentExpanded">
                <td class="stock-tree-table__toggle-cell">
                  @if ((row.level === 'product' || row.level === 'variant') && !row.isEmpty) {
                    <button
                      type="button"
                      class="stock-tree-table__toggle"
                      [attr.aria-label]="row.expanded ? 'Plegar fila' : 'Desplegar fila'"
                      (click)="toggleRow(row)"
                    >
                      {{ row.expanded ? '-' : '+' }}
                    </button>
                  } @else {
                    <span class="stock-tree-table__toggle-spacer"></span>
                  }
                </td>
                <td>{{ row.level === 'product' ? row.brand : emDash }}</td>
                <td>
                  <div class="stock-tree-table__label" [class.stock-tree-table__label--variant]="row.level === 'variant'" [class.stock-tree-table__label--unit]="row.level === 'unit'">
                    <div class="stock-tree-table__primary-text">{{ row.modelLabel }}</div>
                  </div>
                </td>
                <td>
                  @if (row.level === 'unit' && canEditUnitStatus(row) && row.rawStatus !== 'SOLD') {
                    <select
                      class="stock-tree-table__status-select"
                      [attr.data-unit-id]="row.unitId"
                      (change)="onStatusSelect(row, $event)"
                    >
                      @for (option of statusOptionsFor(row); track option) {
                        <option
                          [value]="option"
                          [disabled]="option === row.rawStatus"
                          [selected]="option === row.rawStatus"
                        >{{ formatInventoryUnitStatusLabel(option) }}</option>
                      }
                    </select>
                  } @else {
                    {{ row.status }}
                  }
                </td>
                <td>{{ row.stock }}</td>
                @if (visibleColumns().has('local')) { <td>{{ row.level === 'unit' ? row.local : emDash }}</td> }
                @if (visibleColumns().has('capacidad')) { <td>{{ row.level === 'product' ? emDash : row.capacity }}</td> }
                @if (visibleColumns().has('bateria')) { <td>{{ row.level === 'unit' ? row.battery : emDash }}</td> }
                @if (visibleColumns().has('precio')) { <td>{{ row.level === 'product' ? emDash : row.price }}</td> }
                @if (visibleColumns().has('descuento')) { <td>{{ row.level === 'product' ? emDash : row.discount }}</td> }
                @if (canViewCostPrice() && visibleColumns().has('costo')) { <td>{{ row.level === 'unit' ? row.costPrice : emDash }}</td> }
                @if (visibleColumns().has('color')) { <td>{{ row.level === 'product' ? emDash : row.color }}</td> }
                @if (visibleColumns().has('caja')) { <td>{{ row.level === 'unit' ? row.box : emDash }}</td> }
                @if (visibleColumns().has('imei')) { <td>{{ row.level === 'unit' ? row.imei : emDash }}</td> }
                @if (visibleColumns().has('serie')) { <td>{{ row.level === 'unit' ? row.serial : emDash }}</td> }
                @if (visibleColumns().has('ingreso')) { <td>{{ row.level === 'unit' ? row.receivedAt : emDash }}</td> }
                @if (visibleColumns().has('proveedor')) { <td>{{ row.level === 'product' ? emDash : row.supplier }}</td> }
                @if (visibleColumns().has('reacondicionado')) {
                  <td>{{ row.level === 'unit' ? (row.isRefurbished ? 'SI' : 'NO') : emDash }}</td>
                }
                @if (visibleColumns().has('observacion')) {
                  <td class="stock-tree-table__observation-cell">
                    @if (row.level === 'unit' && row.observation) {
                      <button
                        type="button"
                        class="stock-tree-table__observation-trigger"
                        aria-label="Ver observación"
                        (mouseenter)="openObservationTooltip(row, $event)"
                        (mouseleave)="closeObservationTooltip()"
                        (focus)="openObservationTooltip(row, $event)"
                        (blur)="closeObservationTooltip()"
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="7.5" r="1.25" fill="currentColor"/><rect x="11" y="10" width="1.5" height="7" rx="0.75" fill="currentColor"/></svg>
                      </button>
                    } @else {
                      {{ emDash }}
                    }
                  </td>
                }
                <td class="stock-tree-table__actions-cell app-table__actions-cell" [class.app-table__actions-cell--open]="openMenuId() === row.key">
                  @if (row.actions.length > 0) {
                    <div class="stock-tree-table__actions app-table__actions" [class.app-table__actions--open]="openMenuId() === row.key">
                      <button
                        type="button"
                        class="stock-tree-table__menu-trigger app-table__menu-trigger"
                        aria-label="Abrir acciones"
                        [attr.aria-expanded]="openMenuId() === row.key"
                        (click)="toggleMenu(row, $event)"
                      >
                        ...
                      </button>
                    </div>
                  } @else {
                    {{ emDash }}
                  }
                </td>
              </tr>
              }
            }
          </tbody>
        </table>
      </div>

      @if (openMenuItem(); as row) {
        <div class="stock-tree-table__menu app-table__menu app-table__menu--floating" [style.top.px]="menuPosition()?.top" [style.left.px]="menuPosition()?.left">
          @for (action of row.actions; track action.action) {
            <button
              type="button"
              [disabled]="action.disabled ?? false"
              [attr.title]="action.disabledReason ?? null"
              [attr.aria-label]="action.disabledReason ?? action.label"
              (click)="emitAction(row, action.action)"
            >
              {{ action.label }}
            </button>
          }
        </div>
      }

      @if (observationTooltip(); as tooltip) {
        <div
          class="stock-tree-table__observation-tooltip"
          role="tooltip"
          [style.top.px]="tooltip.top"
          [style.left.px]="tooltip.left"
          (mouseenter)="openObservationTooltip(tooltip.row, $event)"
          (mouseleave)="closeObservationTooltip()"
        >
          {{ tooltip.row.observation }}
        </div>
      }

      @if (rows().length === 0) {
        <p class="stock-tree-table__empty">Sin resultados</p>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        max-width: 100%;
      }

      .stock-tree-table__table {
        min-width: 1480px;
      }

      .stock-tree-table__toggle-column,
      .stock-tree-table__toggle-cell {
        width: 1%;
        min-width: 48px;
        padding-right: 8px;
        padding-left: 12px;
        text-align: center;
      }

      .stock-tree-table__toggle-column,
      .stock-tree-table__toggle-cell {
        position: sticky;
        left: 0;
        z-index: 3;
      }

      .stock-tree-table__toggle-column,
      .stock-tree-table__actions-column {
        background: #faf6f3;
      }

      .stock-tree-table__row--product td {
        background: #fffaf8;
        font-weight: 600;
      }

      .stock-tree-table__row--variant td {
        background: #fffdfc;
      }

      .stock-tree-table__row--product .stock-tree-table__toggle-cell,
      .stock-tree-table__row--product .stock-tree-table__actions-cell {
        background: #fffaf8;
      }

      .stock-tree-table__row--variant .stock-tree-table__toggle-cell,
      .stock-tree-table__row--variant .stock-tree-table__actions-cell {
        background: #fffdfc;
      }

      .stock-tree-table__row--child-of-product td {
        background: color-mix(in srgb, var(--accent) 6%, white);
      }

      .stock-tree-table__row--child-of-product .stock-tree-table__toggle-cell,
      .stock-tree-table__row--child-of-product .stock-tree-table__actions-cell {
        background: color-mix(in srgb, var(--accent) 6%, white);
      }

      .stock-tree-table__row--child-of-product td:first-child {
        border-left: 4px solid var(--accent);
      }

      .stock-tree-table__row--child-of-variant td {
        background: color-mix(in srgb, var(--accent) 10%, white);
      }

      .stock-tree-table__row--child-of-variant .stock-tree-table__toggle-cell,
      .stock-tree-table__row--child-of-variant .stock-tree-table__actions-cell {
        background: color-mix(in srgb, var(--accent) 10%, white);
      }

      .stock-tree-table__row--child-of-variant td:first-child {
        border-left: 4px solid color-mix(in srgb, var(--accent) 85%, white);
      }

      .stock-tree-table__row--sold td {
        color: #7a6b66;
        background: #f5efeb;
      }

      .stock-tree-table__row--sold .stock-tree-table__toggle-cell,
      .stock-tree-table__row--sold .stock-tree-table__actions-cell {
        background: #f5efeb;
      }

      .stock-tree-table__row--sold .stock-tree-table__primary-text {
        color: #7a6b66;
        text-decoration: line-through;
        text-decoration-color: #b9a8a2;
      }

      .stock-tree-table__toggle-column,
      .stock-tree-table__toggle-cell {
        box-shadow: 1px 0 0 #f0e8e3;
      }

      .stock-tree-table__actions-column,
      .stock-tree-table__actions-cell {
        box-shadow: -1px 0 0 #f0e8e3;
      }

      .stock-tree-table__label {
        min-width: 0;
      }

      .stock-tree-table__label--variant {
        padding-left: 20px;
      }

      .stock-tree-table__label--unit {
        padding-left: 40px;
      }

      .stock-tree-table__toggle,
      .stock-tree-table__menu-trigger {
        border: 1px solid #dbc9c2;
        background: #ffffff;
        color: #332b28;
        border-radius: 10px;
        cursor: pointer;
      }

      .stock-tree-table__status-select {
        padding: 4px 8px;
        border: 1px solid #dbc9c2;
        border-radius: 6px;
        background: #ffffff;
        font: inherit;
        font-size: 0.85rem;
        color: #332b28;
        cursor: pointer;
      }

      .stock-tree-table__status-select:focus {
        outline: 2px solid var(--accent);
        outline-offset: 1px;
      }

      .stock-tree-table__toggle {
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        font-weight: 700;
        line-height: 1;
      }

      .stock-tree-table__toggle-spacer {
        width: 28px;
        flex: 0 0 28px;
      }

      .stock-tree-table__primary-text {
        font-weight: 600;
      }

      .stock-tree-table__secondary-text {
        margin-top: 2px;
        color: #7a6b66;
        font-size: 12px;
        font-weight: 500;
      }

      .stock-tree-table__cta-row td {
        padding: 0;
        border-top: 1px solid #f0e8e3;
        background: #fffaf8;
        text-align: center;
      }

      .stock-tree-table__cta-btn {
        display: block;
        min-height: 38px;
        margin: 12px auto;
        padding: 0 18px;
        border: 1px solid var(--accent);
        border-radius: 10px;
        background: var(--accent);
        color: var(--on-accent);
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }

      .stock-tree-table__cta-btn:hover {
        background: color-mix(in srgb, var(--accent) 85%, black);
      }

      .stock-tree-table__observation-cell {
        text-align: center;
        width: 56px;
      }

      .stock-tree-table__observation-trigger {
        background: transparent;
        border: 0;
        padding: 4px;
        cursor: help;
        color: #7a6b66;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
      }

      .stock-tree-table__observation-trigger:hover,
      .stock-tree-table__observation-trigger:focus-visible {
        background: #f3eae5;
      }

      .stock-tree-table__observation-tooltip {
        position: fixed;
        z-index: 200;
        max-width: 320px;
        padding: 8px 12px;
        background: #332b28;
        color: #faf6f3;
        border-radius: 8px;
        font-size: 12px;
        line-height: 1.4;
        box-shadow: 0 6px 18px rgba(0,0,0,0.25);
        white-space: pre-wrap;
        word-break: break-word;
        pointer-events: auto;
      }

      .stock-tree-table__empty {
        margin: 0;
        padding: 24px 16px;
        color: #7a6b66;
        font-size: 0.9rem;
        text-align: center;
      }

    `
  ]
})
export class StockTreeTableComponent extends FloatingTableMenuController<ActionableStockRow> {
  readonly nodes = input<StockTreeNode[]>([]);
  /** Issue #63 — the page owns the checked set: a unit row is visible iff its status is checked. */
  readonly checkedStatuses = input<Set<InventoryUnitStatus>>(new Set<InventoryUnitStatus>(['AVAILABLE']));
  /** Issue #63 — catalog-only products stay in the tree only while Inactivos is checked. */
  readonly includeInactive = input(false);
  readonly priceRange = input<{ min: number; max: number } | null>(null);
  readonly canManageCatalog = input(false);
  readonly canDeleteProduct = input(false);
  readonly visibleColumns = input.required<Set<StockOptInColumnId>>();
  readonly rowAction = output<StockTreeRowActionEvent>();
  readonly createVariantCta = output<string>();
  readonly createUnitCta = output<{ productId: string; variantId: string }>();
  readonly statusChangeRequest = output<{ unitId: string; currentStatus: InventoryUnitStatus; nextStatus: InventoryUnitStatus; unitLabel?: string }>();
  readonly columnCount = computed(() => REQUIRED_COLUMN_COUNT + this.visibleColumns().size);
  readonly emDash = EM_DASH;
  readonly formatInventoryUnitStatusLabel = formatInventoryUnitStatusLabel;
  readonly observationTooltip = signal<{ row: UnitRow; top: number; left: number } | null>(null);
  private readonly expandedProductIds = signal<Set<string>>(new Set());
  private readonly expandedVariantIds = signal<Set<string>>(new Set());
  private readonly authStore = inject(AuthStore);

  readonly canViewCostPrice = computed(() => this.authStore?.actor()?.role === 'SUPER_ADMIN');
  private readonly businessSettingsStore = inject(BusinessSettingsStore);
  readonly formatMoney = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
      return EM_DASH;
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: this.businessSettingsStore.currencyCode(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  readonly canEditUnitStatus = (row: UnitRow): boolean => {
    return resolveOperationalActionVisibility(this.authStore.actor(), row as unknown as import('../inventory/models/inventory.model').InventoryUnitDto);
  };

  readonly statusOptionsFor = (row: UnitRow): InventoryUnitStatus[] => {
    return resolveAllowedStatusTransitions(row.rawStatus);
  };

  readonly visibleNodes = computed<StockTreeNode[]>(() => {
    const checkedStatuses = this.checkedStatuses();
    const includeInactive = this.includeInactive();
    const activePriceRange = this.priceRange();
    const priceFilterActive = activePriceRange !== null;
    const allNodes = this.nodes();
    const catalogOnlyProductIds = new Set(
      allNodes.filter((product) => product.children.length === 0).map((product) => product.productId)
    );
    const orphanVariantIds = new Set(
      allNodes.flatMap((product) =>
        product.children
          .filter((variant) => variant.children.length === 0)
          .map((variant) => variant.variantId)
      )
    );

    return allNodes
      // Issue #63 — the Inactivos gate is hierarchical: an inactive product or
      // variant stays hidden while Inactivos is off, even when it (or its
      // children) would otherwise pass the status and price filters.
      .filter((product) => isCatalogActivityVisible(product.isActive, includeInactive))
      .map((product) => {
        const visibleVariants = product.children
          .filter((variant) => isCatalogActivityVisible(variant.isActive, includeInactive))
          .map((variant) => {
            // Issue #63 — unit visibility is the checked set; the price range
            // (when active) is applied on top of it, never instead of it.
            const checkedUnits = variant.children.filter((unit) => checkedStatuses.has(unit.status));
            const remainingUnits =
              activePriceRange === null
                ? checkedUnits
                : checkedUnits.filter((unit) => isUnitWithinPriceRange(unit, activePriceRange));

            return {
              ...variant,
              quantity: remainingUnits.length,
              children: remainingUnits
            };
          })
          .filter((variant) =>
            priceFilterActive
              ? variant.children.length > 0
              : variant.children.length > 0 || orphanVariantIds.has(variant.variantId)
          );

        return {
          ...product,
          quantity: visibleVariants.reduce((total, variant) => total + variant.quantity, 0),
          children: visibleVariants
        };
      })
      .filter((product) =>
        priceFilterActive
          ? product.children.length > 0
          : product.children.length > 0 || (catalogOnlyProductIds.has(product.productId) && includeInactive)
      );
  });

  readonly rows = computed<StockRow[]>(() => {
    const expandedProducts = this.expandedProductIds();
    const expandedVariants = this.expandedVariantIds();
    const canManageCatalog = this.canManageCatalog();
    const canDeleteProduct = this.canDeleteProduct();
    const nextRows: StockRow[] = [];

    for (const product of this.visibleNodes()) {
      const productKey = `product:${product.productId}`;
      const productExpanded = expandedProducts.has(product.productId);

      const isEmpty = product.children.length === 0;

      const productActions: RowActionItem[] = [];
      if (canManageCatalog) {
        productActions.push({ action: 'edit', label: 'Modificar' });
      }
      if (canDeleteProduct) {
        productActions.push({ action: 'delete', label: 'Eliminar' });
      } else if (canManageCatalog) {
        productActions.push({ action: 'delete', label: product.isActive === false ? 'Restaurar' : 'Eliminar' });
      }

      nextRows.push({
        key: productKey,
        level: 'product',
        productId: product.productId,
        brand: resolveProductBrand(product),
        model: resolveProductModel(product),
        modelLabel: resolveProductModel(product),
        status: EM_DASH,
        stock: product.quantity,
        expanded: productExpanded,
        isEmpty,
        actions: productActions
      });

      if (isEmpty && canManageCatalog) {
        nextRows.push({
          key: `cta-variant:${product.productId}`,
          level: 'variant-cta',
          productId: product.productId,
          brand: resolveProductBrand(product),
          model: resolveProductModel(product)
        });
      }

      if (!productExpanded) {
        continue;
      }

      for (const variant of product.children) {
        const variantKey = `variant:${variant.variantId}`;
        const variantExpanded = expandedVariants.has(variant.variantId);
        const variantLabel = resolveVariantModelLabel(variant);
        const deleteBlocked = variant.hasInventoryUnits === true;

        const variantParentExpanded = expandedProducts.has(product.productId);
        nextRows.push({
          key: variantKey,
          level: 'variant',
          productId: product.productId,
          variantId: variant.variantId,
          modelLabel: variantLabel,
          status: EM_DASH,
          stock: variant.quantity,
          capacity: resolveVariantCapacity(variant),
          price: this.formatMoney(variant.basePrice),
          discount: this.formatMoney(variant.discountPrice),
          color: resolveVariantColor(variant),
          supplier: formatText(variant.supplierName),
          expanded: variantExpanded,
          isEmpty: variant.children.length === 0,
          actions: canManageCatalog
            ? [
                { action: 'edit', label: 'Modificar' },
                {
                  action: 'delete',
                  label: 'Eliminar',
                  ...(deleteBlocked ? { disabled: true, disabledReason: BLOCKED_DELETE_MESSAGE } : {})
                }
              ]
            : [],
          parentExpanded: variantParentExpanded
        });

        if (variant.children.length === 0 && canManageCatalog) {
          nextRows.push({
            key: `cta-unit:${variant.variantId}`,
            level: 'unit-cta',
            productId: product.productId,
            variantId: variant.variantId,
            brand: resolveProductBrand(product),
            model: resolveProductModel(product),
            variantLabel
          });
        }

        if (!variantExpanded) {
          continue;
        }

        const unitParentExpanded = expandedProducts.has(product.productId) && expandedVariants.has(variant.variantId);
        for (const unit of variant.children) {
          nextRows.push({
            key: `unit:${unit.unitId}`,
            level: 'unit',
            productId: product.productId,
            variantId: variant.variantId,
            unitId: unit.unitId,
            stock: '1',
            modelLabel: unit.label,
            local: formatText(unit.storeName ?? unit.storeId),
            capacity: resolveVariantCapacity(variant),
            battery: formatBattery(unit.batteryHealth),
            price: this.formatMoney(unit.price),
            discount: this.formatMoney(unit.discountPrice),
            color: resolveVariantColor(variant),
            box: formatBox(unit.hasBox),
            imei: formatText(unit.imei),
            serial: formatText(unit.serialNumber),
            receivedAt: formatInventoryDate(unit.receivedAt),
            supplier: formatText(unit.supplierName),
            status: formatInventoryUnitStatusLabel(unit.status),
            rawStatus: unit.status,
            isRefurbished: unit.isRefurbished ?? false,
            observation: unit.observation ?? null,
            costPrice: this.formatMoney(unit.costPrice),
            actions: [
              { action: 'edit', label: 'Modificar' },
              { action: 'delete', label: 'Eliminar' }
            ],
            parentExpanded: unitParentExpanded
          });
        }
      }
    }

    return nextRows;
  });

  toggleRow(row: ProductRow | VariantRow): void {
    this.closeMenu();

    if (row.level === 'product') {
      this.expandedProductIds.update((current) => toggleSetEntry(current, row.productId));
      return;
    }

    this.expandedVariantIds.update((current) => toggleSetEntry(current, row.variantId));
  }

  toggleMenu(row: ActionableStockRow, event: MouseEvent): void {
    this.toggleFloatingMenu(row, row.key, event);
  }

  closeMenu(): void {
    this.closeFloatingMenu();
  }

  openObservationTooltip(row: UnitRow, event: MouseEvent | FocusEvent): void {
    const target = (event.currentTarget ?? event.target) as HTMLElement;
    const rect = target.getBoundingClientRect();
    const TOOLTIP_OFFSET = 6;
    const TOOLTIP_WIDTH = 320;
    const top = rect.bottom + TOOLTIP_OFFSET;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - 8));
    this.observationTooltip.set({ row, top, left });
  }

  closeObservationTooltip(): void {
    this.observationTooltip.set(null);
  }

  emitAction(row: StockRow, action: StockRowAction): void {
    const actionItem = 'actions' in row ? row.actions.find((item) => item.action === action) : undefined;

    if (actionItem?.disabled) {
      return;
    }

    this.closeMenu();

    this.rowAction.emit({
      action,
      level: row.level,
      ...(row.level !== 'unit' ? { productId: row.productId } : { productId: row.productId }),
      ...(row.level === 'variant' || row.level === 'unit' ? { variantId: row.variantId } : {}),
      ...(row.level === 'unit' ? { unitId: row.unitId, unitLabel: row.modelLabel } : {})
    });
  }

  onCreateFirstVariant(productId: string): void {
    this.createVariantCta.emit(productId);
  }

  onCreateFirstUnit(productId: string, variantId: string): void {
    this.createUnitCta.emit({ productId, variantId });
  }

  onStatusSelect(row: UnitRow, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const nextStatus = select.value as InventoryUnitStatus;
    if (nextStatus === row.rawStatus) {
      return;
    }
    this.statusChangeRequest.emit({
      unitId: row.unitId,
      currentStatus: row.rawStatus,
      nextStatus,
      unitLabel: row.modelLabel
    });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (this.isFloatingMenuEventTarget(event.target, ['.stock-tree-table__actions', '.stock-tree-table__menu'])) {
      return;
    }

    this.closeMenu();
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.closeMenu();
    this.closeObservationTooltip();
  }
}

const toggleSetEntry = (current: Set<string>, value: string): Set<string> => {
  const next = new Set(current);

  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }

  return next;
};

/** Issue #56 — a unit survives an active price range only when it has a price inside [min, max]. */
const isUnitWithinPriceRange = (
  unit: StockTreeUnitNode,
  range: { min: number; max: number }
): boolean => {
  const price = unit.price;
  return typeof price === 'number' && price >= range.min && price <= range.max;
};

/**
 * Issue #63 — a product/variant is inactive only on an explicit
 * `isActive === false` (the API omits the flag when it is active). Inactive
 * catalog rows become visible only while Inactivos (includeInactive) is on.
 * Exported so the page's price bounds use the exact same gate as the table.
 */
export const isCatalogActivityVisible = (
  isActive: boolean | undefined,
  includeInactive: boolean
): boolean => isActive !== false || includeInactive;

const resolveProductBrand = (product: StockTreeNode): string => {
  return product.brand?.trim() || product.label;
};

const resolveProductModel = (product: StockTreeNode): string => {
  return product.model?.trim() || EM_DASH;
};

const resolveVariantColor = (variant: StockTreeVariantNode): string => {
  return variant.color?.trim() || variant.label.split(' · ')[0]?.trim() || EM_DASH;
};

const resolveVariantCapacity = (variant: StockTreeVariantNode): string => {
  return variant.capacity?.trim() || variant.label.split(' · ')[1]?.trim() || EM_DASH;
};

const resolveVariantModelLabel = (variant: StockTreeVariantNode): string => {
  const color = resolveVariantColor(variant);
  const capacity = resolveVariantCapacity(variant);

  if (color === EM_DASH && capacity === EM_DASH) {
    return formatText(variant.label);
  }

  if (color === EM_DASH) {
    return capacity;
  }

  if (capacity === EM_DASH) {
    return color;
  }

  return `${color} · ${capacity}`;
};

const formatText = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return EM_DASH;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : EM_DASH;
};

const formatBattery = (value: number | null | undefined): string => {
  return value === null || value === undefined ? EM_DASH : `${value}%`;
};

const formatBox = (value: boolean | null | undefined): string => {
  return value === null || value === undefined ? EM_DASH : value ? 'Si' : 'No';
};
